import React from 'react';
import styled from 'styled-components';
import { getCompanyColor } from '~/helpers/getColors';
import { CopyStandard } from '../../atoms/Copytext';
import { CopyBigSkin } from '../../atoms/Copytext/CopytextSkins';
import CompanyLogo from '../../atoms/CompanyLogo';
import Bookmark from '../Bookmark/Bookmark';
import useIsMobile from '~/hooks/useIsMobile';

const StyledLinkWrapper = styled.div`
  background: white;
  width: 100%;
  margin-bottom: 5px;
  padding: 0px 2px;
  display: flex;
  -webkit-box-align: center;
  align-items: center;
  position: relative;
  height: 34px;
`;

const StyledLink = styled.a`
  padding: 0px;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  text-decoration: none;

  .richText {
    margin: 0px !important;
  }
`;

const StyledLinkHeader = styled.div`
  display: grid;
  grid-template-columns: 19% 50% 20% 5%;
  align-items: center;
  gap: 2%;

  & svg {
    max-width: 100%;
  }

  & div,
  span {
    margin-bottom: 0px;
    padding-bottom: 0px;
    font-size: 0.9em;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-bottom: 20px;
`;

/**
 * @param {Object} props
 * @param {CompanyLogoType} props.logo
 */
const CompanyIcon = styled(CompanyLogo)`
  color: ${(props) => getCompanyColor(props.logo)} !important;
  max-width: 70% !important;
  margin-left: 15px;
  height: 100%;
`;

const Location = styled(CopyStandard)`
  display: block;
  padding-bottom: 15px;
  font-size: 0.8em !important;
  color: ${(props) => props.theme.color.gray600} !important;
`;

const BookmarkWrapper = styled.div`
  width: 50%;
`;

/**
 * @param {Object} props
 * @param {Theme} props.theme
 */
const JobTitle = styled.div`
  ${CopyBigSkin};
  text-decoration: none;
  color: ${(props) => props.theme.color.primary800} !important;
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: auto;
  font-size: 1em;

  &:hover {
    color: ${(props) => props.theme.color.cta};
  }
`;

/**
 * Job teaser link component
 * @param {JobTeaserProps} props
 * @returns {React.ReactElement}
 */

export const JobTeaserLink = ({
  title,
  url,
  businessSegment,
  location,
  bookmarkId,
}) => {
  const isMobileSmDown = useIsMobile({ useMq: 'smDown' });
  const isMobileMdDown = useIsMobile({ useMq: 'mdDown' });

  /**
   * Truncates the title based on current breakpoint
   * @param {string} str - The string to truncate
   * @returns {string} - The truncated string
   */
  const truncateTitle = (str) => {
    if (isMobileSmDown) {
      const charsAmount = 77;
      return str.length > charsAmount
        ? `${str.substr(0, charsAmount)}...`
        : str;
    }
    if (isMobileMdDown) {
      const charsAmount = 250;
      return str.length > charsAmount
        ? `${str.substr(0, charsAmount)}...`
        : str;
    }
    const charsAmount = 170;
    return str.length > charsAmount ? `${str.substr(0, charsAmount)}...` : str;
  };

  return (
    <StyledLinkWrapper>
      <StyledLink href={url} title={title} target="_blank">
        <StyledLinkHeader>
          <HeaderContainer>
            {businessSegment && <CompanyIcon logo={businessSegment} hovered />}
          </HeaderContainer>
          <JobTitle>{truncateTitle(title)}</JobTitle>
          {location && <Location>{location}</Location>}
          {bookmarkId ? (
            <BookmarkWrapper onClick={(e) => e.preventDefault()}>
              <Bookmark
                id={bookmarkId}
                city={location}
                company={businessSegment}
                jobId=""
                jobTitle={title}
                context="JobTeaserLink"
              />
            </BookmarkWrapper>
          ) : (
            <div />
          )}
        </StyledLinkHeader>
      </StyledLink>
    </StyledLinkWrapper>
  );
};
